<template>
    <div class="glass-container">
    <div class="users-management-container">
        <!-- <Backer /> -->

        <div class="modern-card">
            <!-- Header -->
            <div class="header-section">
                <div class="title-section">
                    <h1 class="page-title">Users Management</h1>
                    <p class="page-subtitle">Manage user accounts and permissions</p>
                </div>

                <div class="action-buttons">
                    <b-button @click="add" variant="primary" class="action-btn add-btn">
                        <b-icon icon="person-plus" class="mr-2"></b-icon>
                        Add User
                    </b-button>
                    <b-button @click="refresh" variant="outline-secondary" class="action-btn refresh-btn">
                        <b-icon icon="arrow-clockwise" class="mr-2"></b-icon>
                        Refresh
                    </b-button>
                </div>
            </div>

            <!-- Search Box -->
            <div class="search-container mb-4 d-flex justify-content-space-between">
                <b-input-group>
                    <b-input-group-prepend>
                        <b-input-group-text>
                            <b-icon icon="search"></b-icon>
                        </b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input
                        v-model="fullname_filter"
                        placeholder="Search by fullname..."
                        @input="applyFilters"
                    ></b-form-input>
                    <b-input-group-append v-if="fullname_filter">
                        <b-button variant="outline-secondary" @click="fullname_filter = ''; applyFilters()">
                            <b-icon icon="x"></b-icon>
                        </b-button>
                    </b-input-group-append>
                </b-input-group>

                <b-input-group>
                    <b-input-group-prepend>
                        <b-input-group-text>
                            <b-icon icon="person"></b-icon>
                        </b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input
                        v-model="username_filter"
                        placeholder="Search by username..."
                        @input="applyFilters"
                    ></b-form-input>
                    <b-input-group-append v-if="username_filter">
                        <b-button variant="outline-secondary" @click="username_filter = ''; applyFilters()">
                            <b-icon icon="x"></b-icon>
                        </b-button>
                    </b-input-group-append>
                </b-input-group>
            </div>

            <!-- Quick Filters -->
            <div class="quick-filters mb-4">
                <div class="filter-tags">
                    <span
                        v-for="role in quickRoleOptions"
                        :key="role.value"
                        class="filter-tag"
                        :class="{ 'active': userrole_filter === role.value }"
                        @click="toggleQuickFilter('userrole', role.value)"
                    >
                        {{ role.text }}
                    </span>

                    <span
                        class="filter-tag"
                        :class="{ 'active': active_filter === true }"
                        @click="toggleQuickFilter('active', true)"
                    >
                        Active
                    </span>

                    <span
                        class="filter-tag"
                        :class="{ 'active': active_filter === false }"
                        @click="toggleQuickFilter('active', false)"
                    >
                        Inactive
                    </span>

                    <b-button
                        @click="showAdvancedFilters = !showAdvancedFilters"
                        variant="link"
                        size="sm"
                        class="filter-more-btn"
                    >
                        <b-icon :icon="showAdvancedFilters ? 'chevron-up' : 'chevron-down'" class="mr-1"></b-icon>
                        More Filters
                    </b-button>
                </div>
            </div>

            <!-- Advanced Filters -->
            <b-collapse v-model="showAdvancedFilters" class="mb-4">
                <div class="advanced-filters">
                    <div class="filter-row">
                        <div class="filter-group">
                            <label class="filter-label">User Role</label>
                            <b-form-select
                                v-model="userrole_filter"
                                :options="userroles"
                                @change="applyFilters"
                            >
                                <option :value="null">All Roles</option>
                            </b-form-select>
                        </div>

                        <div class="filter-group">
                            <label class="filter-label">Status</label>
                            <b-form-select
                                v-model="active_filter"
                                :options="active_filter_options"
                                @change="applyFilters"
                            >
                                <option :value="null">All Status</option>
                            </b-form-select>
                        </div>

                        <div class="filter-group" v-if="location_filter_options.length > 0">
                            <label class="filter-label">Location</label>
                            <b-form-select
                                v-model="location_filter"
                                :options="location_filter_options"
                                @change="applyFilters"
                            >
                                <option :value="null">All Locations</option>
                            </b-form-select>
                        </div>
                    </div>

                    <div class="filter-actions">
                        <b-button
                            @click="clearFilters"
                            variant="outline-secondary"
                            size="sm"
                            v-if="hasActiveFilters"
                        >
                            <b-icon icon="eraser" class="mr-2"></b-icon>
                            Clear All Filters
                        </b-button>

                        <span class="filter-count" v-if="hasActiveFilters">
                            {{ filteredUsers.length }} users found
                        </span>
                    </div>
                </div>
            </b-collapse>

            <!-- Loading State -->
            <div v-if="items_being_loaded" class="loading-container">
                <loading
                    :active="items_being_loaded"
                    :can-cancel="false"
                    :is-full-page="false"
                ></loading>
            </div>

            <!-- Users Table -->
            <div v-else class="users-table-container">
                <div v-if="filteredUsers.length > 0">
                    <div class="table-responsive">
                        <table class="modern-table">
                            <thead>
                                <tr>
                                    <th class="table-header" style="min-width: 100px;">IMAGE</th>
                                    <th class="table-header" @click="sortby('fullname')" style="min-width: 200px;">
                                        FULLNAME
                                        <span class="sort-indicator" v-if="sort_column === 'fullname'">
                                            {{ sort_asc ? '↑' : '↓' }}
                                        </span>
                                    </th>
                                    <th class="table-header" @click="sortby('username')" style="min-width: 200px;">
                                        USERNAME
                                        <span class="sort-indicator" v-if="sort_column === 'username'">
                                            {{ sort_asc ? '↑' : '↓' }}
                                        </span>
                                    </th>
                                    <th class="table-header" @click="sortby('userrole')" style="min-width: 200px;">
                                        USERROLE
                                        <span class="sort-indicator" v-if="sort_column === 'userrole'">
                                            {{ sort_asc ? '↑' : '↓' }}
                                        </span>
                                    </th>
                                    <th class="table-header" @click="sortby('ts')" style="min-width: 170px;">
                                        ADDED ON
                                        <span class="sort-indicator" v-if="sort_column === 'ts'">
                                            {{ sort_asc ? '↑' : '↓' }}
                                        </span>
                                    </th>
                                    <th class="table-header" @click="sortby('active')" style="min-width: 100px;">
                                        ACTIVE
                                        <span class="sort-indicator" v-if="sort_column === 'active'">
                                            {{ sort_asc ? '↑' : '↓' }}
                                        </span>
                                    </th>
                                    <th class="table-header" @click="sortby('location_id')" style="min-width: 200px;">
                                        LOCATION
                                        <span class="sort-indicator" v-if="sort_column === 'location_id'">
                                            {{ sort_asc ? '↑' : '↓' }}
                                        </span>
                                    </th>
                                    <th class="table-header" style="min-width: 260px;">
                                        MAX DISCOUNTS
                                    </th>
                                    <th class="table-header" style="min-width: 150px;">ACTION</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(user, idx) in filteredUsers" :key="idx" class="table-row">
                                    <td class="image-cell">
                                        <div class="image-container">
                                            <div v-if="getUserImage(user)" class="image-wrapper" @click="showFullImage(user)">
                                                <img
                                                    :src="getUserImage(user)"
                                                    :alt="user.fullname || user.username"
                                                    class="user-image"
                                                    @error="handleImageError($event, user)"
                                                    @load="handleImageLoad($event, user)"
                                                />
                                                <div class="image-overlay">
                                                    <b-icon icon="search"></b-icon>
                                                </div>
                                            </div>
                                            <div v-else class="no-image-placeholder">
                                                <div class="no-image-text">No Image</div>
                                                <b-button
                                                    v-if="isAdmin"
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    @click="openUserImageUpload(user)"
                                                    class="upload-btn"
                                                >
                                                    Upload
                                                </b-button>
                                            </div>
                                        </div>
                                    </td>

                                    <td class="text-cell">
                                        <b-input v-model="user.fullname" :disabled="true" size="sm"></b-input>
                                    </td>

                                    <td class="text-cell">
                                        <b-input v-model="user.username" :disabled="true" size="sm"></b-input>
                                    </td>

                                    <td class="role-cell">
                                        <b-form-select
                                            v-model="user.userrole"
                                            :options="userroles"
                                            :disabled="true"
                                            size="sm"
                                        ></b-form-select>
                                    </td>

                                    <td class="date-cell">
                                        {{ formatDate(user.ts) }}
                                    </td>

                                    <td class="status-cell">
                                        <b-form-select
                                            v-model="user.active"
                                            :options="active_statuses"
                                            :disabled="true"
                                            size="sm"
                                        ></b-form-select>
                                    </td>

                                    <td class="location-cell">
                                        <div v-if="user.multiple_locations_enabled === 'true' && user.location_ids">
                                            <div v-for="locId in parseLocationIds(user.location_ids)" :key="locId" class="location-badge-small">
                                                {{ getLocationName(locId) }}
                                            </div>
                                        </div>
                                        <div v-else>
                                            {{ getLocationName(user.properties && user.properties.location_id) }}
                                        </div>
                                    </td>

                                    <td class="discount-caps-cell">
                                        <span class="cap-chip" :title="'Regular: ' + fmtCap(user.max_discount_regular)">
                                            <b-icon icon="cash"></b-icon>
                                            REG {{ fmtCap(user.max_discount_regular) }}
                                        </span>
                                        <span class="cap-chip is-sale" :title="'Sale: ' + fmtCap(user.max_discount_sale)">
                                            SALE {{ fmtCap(user.max_discount_sale) }}
                                        </span>
                                        <span class="cap-chip is-promo" :title="'Promo: ' + fmtCap(user.max_discount_promo)">
                                            PROMO {{ fmtCap(user.max_discount_promo) }}
                                        </span>
                                        <span class="cap-chip is-clearance" :title="'Clearance: ' + fmtCap(user.max_discount_clearance)">
                                            CLR {{ fmtCap(user.max_discount_clearance) }}
                                        </span>
                                    </td>

                                    <td class="actions-cell">
                                        <div class="action-buttons-wrapper">
                                            <b-button
                                                @click="show_user(user)"
                                                variant="outline-primary"
                                                size="sm"
                                                class="action-btn view-btn"
                                                title="View Details"
                                            >
                                                <b-icon icon="eye"></b-icon> View
                                            </b-button>

                                            <b-button
                                                v-if="isAdmin"
                                                @click="openUserImageUpload(user)"
                                                variant="outline-info"
                                                size="sm"
                                                class="action-btn image-btn"
                                                title="Upload/Change Image"
                                            >
                                                <b-icon icon="image"></b-icon>
                                            </b-button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Table Footer -->
                    <div class="table-footer">
                        <div class="pagination-info">
                            Showing {{ filteredUsers.length }} of {{ users.length }} users
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-else class="empty-state">
                    <div class="empty-state-icon">
                        <b-icon icon="people" font-scale="3"></b-icon>
                    </div>
                    <h3>No Users Found</h3>
                    <p v-if="hasActiveFilters">Try adjusting your filters</p>
                    <p v-else>No users in the system yet</p>
                    <div class="empty-state-actions mt-3">
                        <b-button @click="clearFilters" variant="outline-secondary" v-if="hasActiveFilters">
                            Clear All Filters
                        </b-button>
                        <b-button @click="add" variant="primary" class="ml-2">
                            <b-icon icon="person-plus" class="mr-2"></b-icon>
                            Add First User
                        </b-button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Image Viewer Modal -->
        <b-modal id="modal-user-image-viewer" size="md" hide-footer centered title="User Image">
            <div class="text-center">
                <b-img
                    :src="currentFullSizeImage"
                    fluid
                    alt="Full size user image"
                    style="max-height: 70vh; object-fit: contain; border-radius: 50%;"
                />
                <div class="mt-3">
                    <strong>{{ currentUserName }}</strong>
                </div>
            </div>
            <template #modal-footer>
                <b-button size="sm" variant="secondary" @click="$bvModal.hide('modal-user-image-viewer')">
                    Close
                </b-button>
            </template>
        </b-modal>

        <!-- Image Upload Modal -->
        <b-modal
            id="modal-user-image-upload"
            size="md"
            hide-footer
            centered
            :title="`Upload Image - ${uploadUser ? uploadUser.fullname : ''}`"
            @hidden="resetUserImageUpload"
        >
            <div class="image-upload-container">
                <div v-if="!userImagePreview" class="upload-area" @click="triggerUserFileInput">
                    <div class="upload-placeholder">
                        <b-icon icon="cloud-upload" font-scale="3"></b-icon>
                        <div class="mt-2">Click to select an image</div>
                        <small class="text-muted">or drag and drop</small>
                    </div>
                </div>

                <div v-else class="image-preview-container">
                    <img :src="userImagePreview" alt="Preview" class="preview-image" />
                    <div class="preview-actions mt-2">
                        <b-button size="sm" variant="secondary" @click="clearUserImage">
                            Remove
                        </b-button>
                    </div>
                </div>

                <input
                    ref="userFileInput"
                    type="file"
                    accept="image/*"
                    style="display: none"
                    @change="handleUserFileSelect"
                />

                <div class="upload-info mt-3">
                    <small class="text-muted">
                        Supported formats: JPG, PNG, GIF. Max size: 5MB
                    </small>
                </div>

                <div class="mt-4 text-right">
                    <b-button
                        variant="secondary"
                        @click="$bvModal.hide('modal-user-image-upload')"
                        class="mr-2"
                    >
                        Cancel
                    </b-button>
                    <b-button
                        variant="primary"
                        @click="uploadUserImage"
                        :disabled="!selectedUserFile || uploadingUserImage"
                    >
                        {{ uploadingUserImage ? 'Uploading...' : 'Upload Image' }}
                    </b-button>
                </div>
            </div>
        </b-modal>

        <!-- User Details Modal -->
        <b-modal id="modal-user" scrollable title="User" size="lg" hide-footer @hide="fetch_init">
            <UserView
                :viewdata="userdata"
                :new="false"
                :userDetailsProp="packet.userDetails"
                :locations="locations"
            />
        </b-modal>

        <!-- Add User Modal -->
        <b-modal id="modal-new-user" scrollable title="Add User" size="lg" hide-footer @hide="fetch_init">
            <UserView
                :viewdata="userdata"
                :new="true"
                :userDetailsProp="packet.userDetails"
                :locations="locations"
            />
        </b-modal>
    </div>
</div>
</template>

<script>
    import Backer from '@/components/globals/Backer'
    import Requestor from '@/utilities/Requestor'
    import UserView from '@/components/importables/UserView'
    import Loading from 'vue-loading-overlay';
    import 'vue-loading-overlay/dist/vue-loading.css';

    export default {
        name: "ListUsers",
        components: { Backer, UserView, Loading },
        data: function() {
            return {
                packet: {},
                userdata: { properties: {} },
                users: [],
                filteredUsers: [],
                locations: [],
                modal_index: null,
                uploadUser: null,
                selectedUserFile: null,
                userImagePreview: null,
                uploadingUserImage: false,
                currentFullSizeImage: null,
                currentUserName: null,
                items_being_loaded: false,
                sort_column: 'fullname',
                sort_asc: true,

                fullname_filter: null,
                username_filter: null,
                userrole_filter: null,
                active_filter: null,
                location_filter: null,

                userroles: [
                    { value: "admin", text: "Admin" },
                    { value: "accountmanager", text: "Account Manager" },
                    { value: "officeuser", text: "Office User" },
                    { value: "officemanager", text: "Office Manager" },
                    { value: "locationadmin", text: "Location Admin" },
                    { value: "locationuser", text: "Location User" },
                    { value: "locationstockperson", text: "Location Stock Person" },
                    { value: "salesperson", text: "Sales Person" },
                    { value: "warehouseuser", text: "Warehouse User" },
                    { value: "warehousemanager", text: "Warehouse Manager" },
                    { value: "accountsuser", text: "Accounts User" },
                    { value: "housetruck", text: "House Truck" },
                    { value: "deliverytruck", text: "Delivery Truck" }
                ],
                active_statuses: [
                    { value: true, text: "YES" },
                    { value: false, text: "NO" }
                ],
                active_filter_options: [
                    { value: true, text: "Active" },
                    { value: false, text: "Inactive" }
                ],
                showAdvancedFilters: false
            };
        },
        computed: {
            isAdmin() {
                return this.packet.userDetails && this.packet.userDetails.userrole === 'admin';
            },

            location_filter_options() {
                if (!this.packet.locationsdict) return [];
                return Object.entries(this.packet.locationsdict).map(function(entry) {
                    return {
                        value: entry[0],
                        text: entry[1]
                    };
                });
            },

            hasActiveFilters() {
                return this.fullname_filter ||
                       this.username_filter ||
                       this.userrole_filter !== null ||
                       this.active_filter !== null ||
                       this.location_filter !== null;
            },

            quickRoleOptions() {
                return [
                    { value: "admin", text: "Admin" },
                    { value: "officeuser", text: "Office" },
                    { value: "locationadmin", text: "Location" },
                    { value: "salesperson", text: "Sales" }
                ];
            }
        },
        watch: {
            fullname_filter: function() { this.applyFilters(); },
            username_filter: function() { this.applyFilters(); },
            userrole_filter: function() { this.applyFilters(); },
            active_filter: function() { this.applyFilters(); },
            location_filter: function() { this.applyFilters(); }
        },
        methods: {
            parseLocationIds(locationIds) {
                if (!locationIds) return [];
                if (Array.isArray(locationIds)) return locationIds;
                if (typeof locationIds === 'string') {
                    try {
                        return JSON.parse(locationIds);
                    } catch(e) {
                        return [];
                    }
                }
                return [];
            },

            fmtCap(v) {
                if (v === null || v === undefined || v === '') return '—';
                const n = parseFloat(v);
                if (isNaN(n)) return '—';
                return n.toFixed(1) + '%';
            },

            // Image Methods
            getUserImage: function(user) {
                if (!user || !user.properties || !user.properties.image_url) {
                    return null;
                }
                var imageUrl = user.properties.image_url;
                if (typeof imageUrl === 'string') {
                    imageUrl = imageUrl.trim();
                }
                if (imageUrl === '' || imageUrl === 'null' || imageUrl === 'undefined') {
                    return null;
                }
                if (imageUrl && imageUrl.includes('fmapi.myfurnituremecca.com')) {
                    return 'https://corsproxy.io/?' + encodeURIComponent(imageUrl);
                }
                return imageUrl;
            },

            handleImageError: function(event, user) {
                console.warn('Image failed to load for ' + user.username);
                event.target.style.display = 'none';
            },

            handleImageLoad: function(event, user) {
                console.log('Image loaded successfully for ' + user.username);
            },

            showFullImage: function(user) {
                if (!user) return;

                var imageUrl = this.getUserImage(user);
                if (imageUrl) {
                    this.currentFullSizeImage = imageUrl;
                    this.currentUserName = user.fullname || user.username || 'User Image';
                    this.$bvModal.show('modal-user-image-viewer');
                } else {
                    this.$bvToast.toast(
                        "No image available for this user",
                        { title: "Info", autoHideDelay: 2000, variant: "info" }
                    );
                }
            },

            openUserImageUpload: function(user) {
                this.uploadUser = user;
                this.selectedUserFile = null;
                this.userImagePreview = null;
                this.$bvModal.show('modal-user-image-upload');
            },

            triggerUserFileInput: function() {
                this.$refs.userFileInput.click();
            },

            handleUserFileSelect: function(event) {
                var file = event.target.files[0];
                if (!file) return;

                var validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
                if (!validTypes.includes(file.type)) {
                    this.$bvToast.toast(
                        'Please select a valid image file (JPG, PNG, GIF)',
                        { title: "Invalid File", autoHideDelay: 2000, variant: "warning" }
                    );
                    return;
                }

                if (file.size > 5 * 1024 * 1024) {
                    this.$bvToast.toast(
                        'Image size should be less than 5MB',
                        { title: "File Too Large", autoHideDelay: 2000, variant: "warning" }
                    );
                    return;
                }

                this.selectedUserFile = file;

                var reader = new FileReader();
                var that = this;
                reader.onload = function(e) {
                    that.userImagePreview = e.target.result;
                };
                reader.readAsDataURL(file);
            },

            clearUserImage: function() {
                this.selectedUserFile = null;
                this.userImagePreview = null;
                this.$refs.userFileInput.value = '';
            },

            resetUserImageUpload: function() {
                this.uploadUser = null;
                this.selectedUserFile = null;
                this.userImagePreview = null;
                this.uploadingUserImage = false;
                if (this.$refs.userFileInput) {
                    this.$refs.userFileInput.value = '';
                }
            },

            uploadUserImage: function() {
                var that = this;
                if (!this.selectedUserFile || !this.uploadUser) return;

                this.uploadingUserImage = true;

                this.fileToBase64(this.selectedUserFile)
                    .then(function(base64Image) {
                        var path = "/api/user/upload_user_image/";
                        var data = {
                            user_id: that.uploadUser.id,
                            username: that.uploadUser.username,
                            image_data: base64Image
                        };

                        return new Promise(function(resolve, reject) {
                            Requestor.postSyncP(path, data, that, resolve, reject);
                        });
                    })
                    .then(function(response) {
                        if (response.data.error) {
                            throw new Error(response.data.error);
                        }

                        var updatedUser = response.data.result;
                        that.updateUserInList(updatedUser);

                        that.$bvToast.toast(
                            "User image uploaded successfully",
                            { title: "Success", autoHideDelay: 2000, variant: "success" }
                        );

                        that.$bvModal.hide('modal-user-image-upload');
                    })
                    .catch(function(error) {
                        console.error('Error uploading user image:', error);
                        that.$bvToast.toast(
                            error.message || "Error uploading image",
                            { title: "Error", autoHideDelay: 2000, variant: "danger" }
                        );
                    })
                    .finally(function() {
                        that.uploadingUserImage = false;
                    });
            },

            updateUserInList: function(updatedUser) {
                var userIndex = this.users.findIndex(function(user) {
                    return user.id === updatedUser.id;
                });

                if (userIndex !== -1) {
                    if (!this.users[userIndex].properties) {
                        this.users[userIndex].properties = {};
                    }
                    this.users[userIndex].properties.image_url = updatedUser.image_url;
                    this.users = this.users.slice();
                    this.applyFilters();
                }
            },

            fileToBase64: function(file) {
                return new Promise(function(resolve, reject) {
                    var reader = new FileReader();
                    reader.readAsDataURL(file);
                    reader.onload = function() {
                        resolve(reader.result);
                    };
                    reader.onerror = function(error) {
                        reject(error);
                    };
                });
            },

            // Filter Methods
            applyFilters() {
                var filtered = this.users;

                if (this.fullname_filter) {
                    var searchTerm = this.fullname_filter.toLowerCase();
                    filtered = filtered.filter(function(user) {
                        return user.fullname && user.fullname.toLowerCase().includes(searchTerm);
                    });
                }

                if (this.username_filter) {
                    var searchTerm = this.username_filter.toLowerCase();
                    filtered = filtered.filter(function(user) {
                        return user.username && user.username.toLowerCase().includes(searchTerm);
                    });
                }

                if (this.userrole_filter !== null) {
                    filtered = filtered.filter(function(user) {
                        return user.userrole === this.userrole_filter;
                    }.bind(this));
                }

                if (this.active_filter !== null) {
                    filtered = filtered.filter(function(user) {
                        return user.active === this.active_filter;
                    }.bind(this));
                }

                if (this.location_filter !== null) {
                    filtered = filtered.filter(function(user) {
                        if (user.multiple_locations_enabled === 'true' && user.location_ids && user.location_ids.length) {
                            let locationIds = user.location_ids;
                            if (typeof locationIds === 'string') {
                                try {
                                    locationIds = JSON.parse(locationIds);
                                } catch(e) {
                                    locationIds = [];
                                }
                            }
                            return locationIds.includes(parseInt(this.location_filter));
                        }
                        return user.properties &&
                               parseInt(user.properties.location_id) === parseInt(this.location_filter);
                    }.bind(this));
                }

                this.filteredUsers = filtered;
            },

            clearFilters: function() {
                this.fullname_filter = null;
                this.username_filter = null;
                this.userrole_filter = null;
                this.active_filter = null;
                this.location_filter = null;
                this.filteredUsers = this.users;
            },

            toggleQuickFilter: function(type, value) {
                if (type === 'userrole') {
                    if (this.userrole_filter === value) {
                        this.userrole_filter = null;
                    } else {
                        this.userrole_filter = value;
                    }
                } else if (type === 'active') {
                    if (this.active_filter === value) {
                        this.active_filter = null;
                    } else {
                        this.active_filter = value;
                    }
                }
                this.applyFilters();
            },

            // User Actions
            show_user: function(params) {
                var that = this;
                var item = { value: params.id };
                var filtered = this.filteredUsers.filter(function(x) {
                    return x.id == item.value;
                });
                if (filtered.length > 0) {
                    this.userdata = filtered[0];
                    that.$bvModal.show("modal-user");
                }
            },

            fetch_init: function() {
                var that = this;
                var path = "/api/user/list_users/";
                var data = {};

                var success = function(response) {
                    if (response.data.error) {
                        that.$bvToast.toast(
                            response.data.error,
                            { title: "Error", autoHideDelay: 1500, variant: "warning" }
                        );
                    } else {
                        that.users = response.data.result;
                        that.filteredUsers = response.data.result;
                        that.applyFilters();
                    }
                    that.items_being_loaded = false;
                };

                var failure = function(error) {
                    console.log(error);
                    that.$bvToast.toast(
                        "Error loading users. Please refresh.",
                        { title: "Error", autoHideDelay: 1500, variant: "warning" }
                    );
                    that.items_being_loaded = false;
                };

                this.items_being_loaded = true;
                Requestor.getSyncP(path, data, this, success, failure);
            },

            add: function(e) {
                e.preventDefault();
                this.userdata = { properties: {} };
                this.$bvModal.show("modal-new-user");
            },

            refresh: function(e) {
                e.preventDefault();
                this.clearFilters();
                this.fetch_init();
            },

            sortby: function(key) {
                if (this.sort_column === key) {
                    this.sort_asc = !this.sort_asc;
                } else {
                    this.sort_column = key;
                    this.sort_asc = true;
                }

                var mapper;
                if (key === 'ts') {
                    mapper = function(value) { return new Date(value); };
                } else if (key === 'location_id') {
                    mapper = function(value, user) {
                        return this.getLocationName(user.properties && user.properties.location_id);
                    }.bind(this);
                }

                if (this.sort_asc) {
                    this.filteredUsers = Requestor.sort(this.filteredUsers, key, mapper);
                } else {
                    this.filteredUsers = Requestor.sort_reverse(this.filteredUsers, key, mapper);
                }
            },

            // Helper Methods
            getLocationName(locationId) {
                if (!locationId || !this.packet.locationsdict) return 'N/A';
                if (Array.isArray(locationId)) {
                    return locationId.map(id => this.packet.locationsdict[id] || 'N/A').join(', ');
                }
                if (typeof locationId === 'string' && locationId.startsWith('[')) {
                    try {
                        const ids = JSON.parse(locationId);
                        return ids.map(id => this.packet.locationsdict[id] || 'N/A').join(', ');
                    } catch(e) {
                        return this.packet.locationsdict[locationId] || 'N/A';
                    }
                }
                return this.packet.locationsdict[locationId] || 'N/A';
            },

            formatDate: function(dateString) {
                if (!dateString) return 'N/A';
                try {
                    var date = new Date(dateString);
                    return date.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                    });
                } catch (error) {
                    return dateString;
                }
            }
        },
        created: function() {
            this.packet = JSON.parse(localStorage.getItem('packet')) || {};
            this.packet.userDetails = JSON.parse(localStorage.getItem('userDetails')) || {};
            this.fetch_init();
        }
    };
</script>

<style scoped>
.glass-container {
    min-height: 100vh;
    padding: 20px;
    background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
    background-size: cover;
    background-position: center;
    background-attachment: fixed;
    position: relative;
    overflow: hidden;
}

.glass-container::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    backdrop-filter: blur(2px);
    z-index: 0;
}

.users-management-container {
    padding: 20px;
    background: transparent;
    min-height: 100vh;
    border-radius: 34px;
    z-index: 1;
    position: relative;
}

.modern-card {
    background: rgba(255, 255, 255, 0.7);
    border-radius: 15px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    padding: 25px;
    margin: 0 auto;
    margin-top: 10px;
    max-width: 1962px;
}

/* Header */
.header-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 25px;
    padding-bottom: 20px;
    border-bottom: 2px solid #f0f0f0;
}

.title-section .page-title {
    font-size: 1.8rem;
    font-weight: 700;
    color: #2d3748;
    margin: 0;
}

.title-section .page-subtitle {
    color: #718096;
    font-size: 0.95rem;
    margin: 5px 0 0;
}

.action-buttons {
    display: flex;
    gap: 10px;
}

.action-btn {
    padding: 8px 16px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
}

.add-btn {
    background: #667eea;
    border: none;
}

.add-btn:hover {
    background: #5a67d8;
    transform: translateY(-1px);
}

.refresh-btn:hover {
    transform: translateY(-1px);
}

/* Search */
.search-container {
    margin-bottom: 20px;
    gap: 10px;
}

/* Quick Filters */
.quick-filters {
    margin-bottom: 20px;
}

.filter-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
}

.filter-tag {
    padding: 6px 12px;
    background: #f7fafc;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    color: #4a5568;
}

.filter-tag:hover {
    background: #edf2f7;
    border-color: #cbd5e0;
}

.filter-tag.active {
    background: #667eea;
    color: white;
    border-color: #667eea;
}

.filter-more-btn {
    color: #718096;
    font-size: 0.85rem;
}

/* Advanced Filters */
.advanced-filters {
    background: #f8fafc;
    border-radius: 8px;
    padding: 20px;
    border: 1px solid #e2e8f0;
}

.filter-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
    margin-bottom: 15px;
}

.filter-group {
    display: flex;
    flex-direction: column;
}

.filter-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 5px;
}

.filter-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 15px;
    border-top: 1px solid #e2e8f0;
}

.filter-count {
    font-size: 0.9rem;
    color: #718096;
    font-weight: 500;
}

/* Loading */
.loading-container {
    text-align: center;
    padding: 40px 20px;
}

/* Table */
.table-responsive {
    overflow-x: auto;
    margin-bottom: 20px;
}

.modern-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
}

.table-header {
    padding: 12px 15px;
    text-align: left;
    font-weight: 600;
    color: #4a5568;
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
    cursor: pointer;
    white-space: nowrap;
    user-select: none;
}

.sort-indicator {
    font-size: 0.8rem;
    color: #667eea;
    margin-left: 5px;
}

.table-row {
    transition: background 0.2s ease;
}

.table-row:hover {
    background: #f8fafc;
}

.table-row td {
    padding: 10px 15px;
    border-bottom: 1px solid #e2e8f0;
    vertical-align: middle;
}

/* Image Cell */
.image-cell {
    text-align: center;
    width: 100px;
}

.image-container {
    position: relative;
    display: inline-block;
}

.image-wrapper {
    position: relative;
    display: inline-block;
    cursor: pointer;
}

.user-image {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    object-fit: cover;
    transition: transform 0.2s ease;
}

.user-image:hover {
    transform: scale(1.05);
}

.image-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.3s ease;
    cursor: pointer;
    border-radius: 50%;
    color: white;
}

.image-wrapper:hover .image-overlay {
    opacity: 1;
}

.no-image-placeholder {
    width: 60px;
    height: 60px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border: 1px dashed #ddd;
    border-radius: 50%;
    background: #f9f9f9;
    margin: 0 auto;
}

.no-image-text {
    color: #999;
    font-size: 10px;
}

.upload-btn {
    margin-top: 5px;
    font-size: 10px;
}

/* Text Cells */
.text-cell {
    min-width: 200px;
}

.text-cell .form-control {
    background: #f8f9fa;
    border-color: #e2e8f0;
}

/* Role Cell */
.role-cell {
    min-width: 200px;
}

.role-cell .custom-select {
    background: #f8f9fa;
    border-color: #e2e8f0;
}

/* Date Cell */
.date-cell {
    color: #718096;
    font-size: 0.85rem;
    white-space: nowrap;
}

/* Status Cell */
.status-cell {
    min-width: 100px;
}

.status-cell .custom-select {
    background: #f8f9fa;
    border-color: #e2e8f0;
}

/* Location Cell */
.location-cell {
    color: #4a5568;
    white-space: nowrap;
}

.location-badge-small {
    background: #edf2f7;
    color: #4a5568;
    font-size: 0.75rem;
    padding: 2px 8px;
    border-radius: 10px;
    display: inline-block;
    margin: 2px 2px 0 0;
    border: 1px solid #e2e8f0;
}

/* NEW: Discount Caps Cell */
.discount-caps-cell {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    min-width: 240px;
}

.cap-chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 99px;
    background: #EDF2F7;
    color: #4A5568;
    border: 1px solid #E2E8F0;
    white-space: nowrap;
    letter-spacing: 0.02em;
}

.cap-chip .b-icon {
    font-size: 10px;
    opacity: 0.7;
}

.cap-chip.is-sale {
    background: #E2F2F0;
    color: #0B5552;
    border-color: #BFE1DD;
}

.cap-chip.is-promo {
    background: #FFF1E4;
    color: #B5520B;
    border-color: #F5CFAE;
}

.cap-chip.is-clearance {
    background: #FCEBE8;
    color: #8E2A21;
    border-color: #F3C9C2;
}

/* Actions Cell */
.actions-cell {
    white-space: nowrap;
}

.action-buttons-wrapper {
    display: flex;
    gap: 5px;
    align-items: center;
}

.action-btn {
    padding: 6px 12px;
    font-size: 0.85rem;
    display: flex;
    align-items: center;
    gap: 5px;
    border-radius: 6px;
    transition: all 0.2s ease;
}

.view-btn {
    color: #4299e1;
    border-color: #4299e1;
}

.view-btn:hover {
    background: #4299e1;
    color: white;
}

.image-btn {
    color: #9f7aea;
    border-color: #9f7aea;
    padding: 6px;
}

.image-btn:hover {
    background: #9f7aea;
    color: white;
}

/* Table Footer */
.table-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px 0;
    margin-top: 15px;
    border-top: 1px solid #e2e8f0;
}

.pagination-info {
    color: #718096;
    font-size: 0.85rem;
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 40px 20px;
    border-radius: 8px;
    background: #f8fafc;
    border: 1px dashed #e2e8f0;
}

.empty-state-icon {
    color: #a0aec0;
    margin-bottom: 15px;
}

.empty-state h3 {
    color: #2d3748;
    margin-bottom: 8px;
    font-weight: 600;
}

.empty-state p {
    color: #718096;
    margin-bottom: 15px;
}

.empty-state-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    flex-wrap: wrap;
}

.empty-state-actions .ml-2 {
    margin-left: 10px;
}

/* Image Upload Modal Styles */
.image-upload-container {
    padding: 15px;
}

.upload-area {
    border: 2px dashed #dee2e6;
    border-radius: 50%;
    padding: 40px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s ease;
    background-color: #f8f9fa;
    width: 200px;
    height: 200px;
    margin: 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
}

.upload-area:hover {
    border-color: #007bff;
    background-color: #e9ecef;
}

.upload-placeholder {
    color: #6c757d;
}

.preview-image {
    border-radius: 50%;
    border: 1px solid #dee2e6;
    object-fit: cover;
    max-height: 200px;
    display: block;
    margin: 0 auto;
}

/* Responsive */
@media (max-width: 768px) {
    .modern-card { padding: 15px; }
    .header-section { flex-direction: column; align-items: flex-start; gap: 15px; }
    .action-buttons { width: 100%; }
    .action-btn { flex: 1; justify-content: center; }
    .filter-row { grid-template-columns: 1fr; }
    .filter-actions { flex-direction: column; gap: 10px; align-items: stretch; }
    .table-responsive { font-size: 0.8rem; }
    .table-header, .table-row td { padding: 8px 10px; }
    .empty-state-actions { flex-direction: column; align-items: center; }
    .empty-state-actions .ml-2 { margin-left: 0 !important; margin-top: 10px; }
}

@media (max-width: 480px) {
    .action-buttons { flex-direction: column; }
    .action-buttons-wrapper { flex-direction: column; gap: 5px; }
    .action-btn { width: 100%; justify-content: center; }
}
</style>


// WEBPACK FOOTER //
// src/components/Users/ListUsers.vue